/**
 * The `__ccg` array dispatcher (DESIGN.md §4.1, refactored to avoid CORS).
 *
 * Each widget instance pasted onto a venue calls
 *   (window.__ccg = window.__ccg || []).push({ widget, version, settings, host? })
 * and we are loaded once per page (browser dedupes the shared <script src>).
 *
 * Loading per-widget modules: we use `<script src>` injection (NOT dynamic
 * `import()`) so we don't need CORS on the CDN — `<script>` tags don't trigger
 * CORS preflights, but ES module imports do. Each widget bundle is built as
 * an IIFE that calls `window.__ccg_register(id, version, def)` at the end —
 * which is how the dispatcher gets the widget's `apply()` function.
 */

import type { RuntimeDef } from './runtime-kit';

export interface Registration {
  widget: string;
  version: number;
  settings: unknown;
  host?: HTMLElement;
}

type WidgetDefAny = RuntimeDef<unknown>;
type Pending = { resolve: (def: WidgetDefAny) => void; reject: (err: Error) => void };
type CcgQueue = Registration[] | { push: (r: Registration) => void };

interface CcgWindow extends Window {
  __ccg?: CcgQueue;
  /** Set by the dispatcher; called by widget IIFEs at end-of-load. */
  __ccg_register?: (id: string, version: number, def: WidgetDefAny) => void;
  /** key = `${id}@${version}` → loaded widget runtime def */
  __ccg_loaded?: Map<string, WidgetDefAny>;
  /** key = `${id}@${version}` → consumers waiting for the widget to load */
  __ccg_pending?: Map<string, Pending[]>;
  /** Keys we've already injected a <script> for (dedupe so two instances
   *  of the same widget on a page only fetch the bundle once). */
  __ccg_inflight?: Set<string>;
}

export interface DispatchOpts {
  /** Resolve a widget id + version to its CDN URL. */
  widgetUrl: (id: string, version: number) => string;
}

export async function dispatch(opts: DispatchOpts): Promise<void> {
  const w = window as CcgWindow;

  // Idempotent global init — dispatch may be called more than once if the
  // runtime script gets included multiple times by mistake.
  w.__ccg_loaded ??= new Map();
  w.__ccg_pending ??= new Map();
  w.__ccg_inflight ??= new Set();

  // Install the register hook so widget IIFEs can call it.
  w.__ccg_register = (id, version, def) => {
    const key = `${id}@${version}`;
    w.__ccg_loaded!.set(key, def);
    const queue = w.__ccg_pending!.get(key);
    if (queue) {
      w.__ccg_pending!.delete(key);
      for (const p of queue) p.resolve(def);
    }
  };

  // Snapshot anything pushed before we loaded.
  const pending = Array.isArray(w.__ccg) ? w.__ccg.slice() : [];

  // Replace with a live dispatcher so future pushes route immediately.
  w.__ccg = {
    push: (r: Registration) => {
      void handle(r, opts);
    },
  };

  for (const reg of pending) {
    await handle(reg, opts);
  }
}

async function handle(reg: Registration, opts: DispatchOpts): Promise<void> {
  try {
    const def = await loadWidget(reg.widget, reg.version, opts);
    def.apply(reg.settings, reg.host);
  } catch (err) {
    // Never throw on customer venues — the runtime is best-effort.
    // eslint-disable-next-line no-console
    console.error('[ccg] failed to render', reg.widget, '@', reg.version, err);
  }
}

function loadWidget(id: string, version: number, opts: DispatchOpts): Promise<WidgetDefAny> {
  const w = window as CcgWindow;
  const key = `${id}@${version}`;

  const cached = w.__ccg_loaded?.get(key);
  if (cached) return Promise.resolve(cached);

  return new Promise<WidgetDefAny>((resolve, reject) => {
    let queue = w.__ccg_pending!.get(key);
    if (!queue) {
      queue = [];
      w.__ccg_pending!.set(key, queue);
    }
    queue.push({ resolve, reject });

    // Already injected a <script> for this widget — just wait on the existing one.
    if (w.__ccg_inflight!.has(key)) return;

    w.__ccg_inflight!.add(key);
    const script = document.createElement('script');
    script.src = opts.widgetUrl(id, version);
    script.async = true;
    script.onerror = () => {
      const err = new Error(`Failed to load widget ${id}@v${version}`);
      const waiting = w.__ccg_pending!.get(key);
      w.__ccg_pending!.delete(key);
      w.__ccg_inflight!.delete(key);
      for (const p of waiting ?? []) p.reject(err);
    };
    document.head.appendChild(script);
  });
}
